import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { FormikErrors } from "formik";
import { AttributeTypes, LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { AttributeStatusTypes } from '@enums/attribute-status-types';
import { OOBStatusTypes } from '@enums/oob-status-types';
import { getActiveLanguage, getLanguageContent } from '@redux/selectors/language-selectors';
import { AttributeFormState } from '@idw-types/state/attribute-form-state';
import { FrontAndBackAttributeImages } from '../../front-and-back-attribute-images/front-and-back-attribute-images';
import { SingleAttributeImage } from '../../single-attribute-image/single-attribute-image';
import { HiddenAttributeImage } from '../../hidden-attribute-image/hidden-attribute-image';
import { isFrontAndBackImageAttribute, isHiddenAttribute, isSingleImageAttribute } from '@utils/attribute-input-util';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMobileAlt } from "@fortawesome/free-solid-svg-icons/faMobileAlt";
import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";
import get from "lodash/get";
import styles from "./async-attribute-input.less";

type AsyncAttributeInputProps = {
    fieldName: string;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void> | Promise<FormikErrors<any>>;
    setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
    attribute: AttributeFormState;
    onSetSubmitValue: (payload: any) => void;
    onSetPickerValue: (payload: any) => void;
};

export const AsyncAttributeInput = (props: AsyncAttributeInputProps) => {
    const {
        attribute,
        fieldName,
        setFieldValue,
        setFieldTouched,
        onSetSubmitValue,
        onSetPickerValue
    } = props;

    const activeLanguage = useSelector(getActiveLanguage);
    const content = useSelector(getLanguageContent);

    const {
        unableToVerifyAsync,
        asyncStatusMessage,
        isValueFromPicker,
        oobStatus: attributeOOBStatus,
        status,
        pickerDefaultAttribute,
        type
    } = attribute;

    const canViewGallery = () => {
        if (unableToVerifyAsync) {
            return true;
        } else {
            return (
                [OOBStatusTypes.COMPLETE, OOBStatusTypes.WAIT].includes(attributeOOBStatus) &&
                [AttributeStatusTypes.PICKER, AttributeStatusTypes.NEW, AttributeStatusTypes.PASS].includes(status)
            );
        }
    };

    const singleImageAttributeName = useMemo(() => {
        switch (type) {
            case AttributeTypes.PASSPORT_IMAGE:
                return "passportImage";
            case AttributeTypes.CAMERA_SNAPSHOT:
                return "cameraSnapshot";
            case AttributeTypes.AUDIT_TRAIL_IMAGE:
                return "auditTrailImages";
            default:
                return "";
        }
    }, [type]);

    return (
        <div id={`async-attr-${attribute.id}`} className={styles.asyncAttributeInput}>
            {[OOBStatusTypes.INCOMPLETE].includes(attributeOOBStatus) && (
                <div className={styles.asyncMobile}>
                    <FontAwesomeIcon icon={faMobileAlt} className={styles.mobileIcon} />
                    {content.form.asyncInput.captureMessage}
                </div>
            )}
            {canViewGallery() && isFrontAndBackImageAttribute(type) && (
                <FrontAndBackAttributeImages
                    onSetSubmitValue={onSetSubmitValue}
                    onSetPickerValue={onSetPickerValue}
                    attribute={attribute}
                    fieldName={fieldName}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                />
            )}
            {canViewGallery() && isSingleImageAttribute(type) && (
                <SingleAttributeImage
                    onSetSubmitValue={onSetSubmitValue}
                    onSetPickerValue={onSetPickerValue}
                    attribute={attribute}
                    fieldName={fieldName}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    attributeName={singleImageAttributeName}
                />
            )}
            {canViewGallery() && isHiddenAttribute(type) && (
                <HiddenAttributeImage
                    onSetSubmitValue={onSetSubmitValue}
                    onSetPickerValue={onSetPickerValue}
                    attribute={attribute}
                    fieldName={fieldName}
                    setFieldValue={setFieldValue}
                />
            )}
            {isValueFromPicker && activeLanguage === LocaleTypes.ENGLISH && (
                <span className="async-masked" id={`async-masked-value-${attribute.id}`}>
                    {pickerDefaultAttribute.masked}
                </span>
            )}
            {([OOBStatusTypes.INCOMPLETE, OOBStatusTypes.COMPLETE, OOBStatusTypes.WAIT].includes(attributeOOBStatus) ||
                [AttributeStatusTypes.ASYNC_INCOMPLETE].includes(status)) && (
                    <div className={styles.statusDiv}>
                        <span className={styles.statusText}>{content.form.asyncInput.status}:</span>
                        <span id={`async-status-value-${attribute.id}`}>
                            {get(content, asyncStatusMessage)}
                            {attributeOOBStatus === OOBStatusTypes.COMPLETE && !unableToVerifyAsync && (
                                <FontAwesomeIcon className={styles.checkIcon} icon={faCheck} />
                            )}
                        </span>
                    </div>
                )}
        </div>
    );
};
